import type {
  PlotMaterialIdV1,
  ProjectDocumentV1,
  TransformMmV1,
  TubeProductV1,
} from '../../contracts/src/index.ts';

export const HEIGHT_MIN_CM = 3;
export const HEIGHT_MAX_CM = 60;

export const TUBE_PRESETS = [
  { code: '1ahn', diameterMm: 45, developmentBaseMm: 152 },
  { code: '2ahnr', diameterMm: 60, developmentBaseMm: 200 },
  { code: '83hn', diameterMm: 83, developmentBaseMm: 273 },
  { code: '92hn', diameterMm: 92, developmentBaseMm: 301 },
  { code: '100bh', diameterMm: 100, developmentBaseMm: 330 },
  { code: '120hn', diameterMm: 120, developmentBaseMm: 390 },
  { code: '158bh', diameterMm: 158, developmentBaseMm: 511 },
] as const;

export type TubePreset = (typeof TUBE_PRESETS)[number];
export type TubePresetCode = TubePreset['code'];

export const DEFAULT_TUBE_PRESET_CODE: TubePresetCode = '83hn';

export type CanvasCalibrationEvidence = 'tested' | 'interpolated' | 'extrapolated';

export const CANVAS_DEVELOPMENT_CALIBRATION = {
  '1ahn': { developmentEffectiveMm: 156, evidence: 'tested' },
  '2ahnr': { developmentEffectiveMm: 204, evidence: 'tested' },
  '83hn': { developmentEffectiveMm: 277, evidence: 'tested' },
  '92hn': { developmentEffectiveMm: 306, evidence: 'interpolated' },
  '100bh': { developmentEffectiveMm: 336, evidence: 'interpolated' },
  '120hn': { developmentEffectiveMm: 398, evidence: 'tested' },
  '158bh': { developmentEffectiveMm: 523, evidence: 'extrapolated' },
} as const satisfies Record<TubePresetCode, {
  readonly developmentEffectiveMm: number;
  readonly evidence: CanvasCalibrationEvidence;
}>;

export const PLOT_MATERIALS = [
  {
    id: 'photo-paper',
    label: 'Papel fotográfico',
    compactLabel: 'Papel',
    isDefault: true,
  },
  {
    id: 'matte-canvas',
    label: 'Lona mate ecosolvente',
    compactLabel: 'Lona mate',
    isDefault: false,
  },
  {
    id: 'glossy-canvas',
    label: 'Lona brillante ecosolvente',
    compactLabel: 'Lona brillante',
    isDefault: false,
  },
] as const satisfies readonly {
  readonly id: PlotMaterialIdV1;
  readonly label: string;
  readonly compactLabel: string;
  readonly isDefault: boolean;
}[];

export type PlotMaterial = (typeof PLOT_MATERIALS)[number];
export type PlotMaterialId = PlotMaterial['id'];

export const DEFAULT_PLOT_MATERIAL_ID: PlotMaterialId = 'photo-paper';

export type HeightValidationErrorCode =
  | 'required'
  | 'format'
  | 'below-minimum'
  | 'above-maximum';

export type HeightValidationResult =
  | {
      readonly ok: true;
      readonly centimeters: number;
      readonly millimeters: number;
    }
  | {
      readonly ok: false;
      readonly code: HeightValidationErrorCode;
      readonly message: string;
    };

export function parseHeightCm(input: string | number | null | undefined): HeightValidationResult {
  const normalized = String(input ?? '').replace(',', '.').trim();

  if (normalized.length === 0) {
    return { ok: false, code: 'required', message: 'Ingresa el alto.' };
  }

  const centimeters = Number(normalized);
  if (!Number.isFinite(centimeters)) {
    return {
      ok: false,
      code: 'format',
      message: 'Usa un numero valido, con coma o punto decimal.',
    };
  }

  if (centimeters < HEIGHT_MIN_CM) {
    return {
      ok: false,
      code: 'below-minimum',
      message: 'El alto minimo es ' + HEIGHT_MIN_CM + ' cm.',
    };
  }

  if (centimeters > HEIGHT_MAX_CM) {
    return {
      ok: false,
      code: 'above-maximum',
      message: 'El alto maximo es ' + HEIGHT_MAX_CM + ' cm.',
    };
  }

  return {
    ok: true,
    centimeters,
    millimeters: Math.round(centimeters * 10),
  };
}

export function formatHeightCm(centimeters: number): string {
  return String(centimeters).replace('.', ',');
}

export function findTubePreset(code: string): TubePreset | undefined {
  return TUBE_PRESETS.find((preset) => preset.code === code);
}

export function getTubePreset(code: string): TubePreset {
  const preset = findTubePreset(code);
  if (!preset) {
    throw new Error('Preset de tubo desconocido: ' + code);
  }
  return preset;
}

export function findPlotMaterial(id: string): PlotMaterial | undefined {
  return PLOT_MATERIALS.find((material) => material.id === id);
}

export function getPlotMaterial(id: string): PlotMaterial {
  const material = findPlotMaterial(id);
  if (!material) {
    throw new Error('Material de Ploteo desconocido: ' + id);
  }
  return material;
}

export function calculateEffectiveDevelopmentMm(
  preset: TubePreset,
  material: PlotMaterial,
): number {
  return material.id === 'photo-paper'
    ? preset.developmentBaseMm
    : CANVAS_DEVELOPMENT_CALIBRATION[preset.code].developmentEffectiveMm;
}

export function calculateMaterialIncrementMm(
  preset: TubePreset,
  material: PlotMaterial,
): number {
  return calculateEffectiveDevelopmentMm(preset, material) - preset.developmentBaseMm;
}

export function calculateExtensionPerSideMm(
  preset: TubePreset,
  material: PlotMaterial,
): number {
  return calculateMaterialIncrementMm(preset, material) / 2;
}

export function createTubeProductV1(
  preset: TubePreset,
  material: PlotMaterial,
  heightMm: number,
): TubeProductV1 {
  if (!Number.isFinite(heightMm) || heightMm <= 0) {
    throw new Error('El alto fisico debe ser mayor que 0 mm.');
  }

  const developmentEffectiveMm = calculateEffectiveDevelopmentMm(preset, material);
  const materialIncrementMm = developmentEffectiveMm - preset.developmentBaseMm;

  return {
    kind: 'tube',
    presetCode: preset.code,
    diameterMm: preset.diameterMm,
    materialId: material.id,
    heightMm,
    developmentBaseMm: preset.developmentBaseMm,
    materialIncrementMm,
    developmentEffectiveMm,
    extensionPerSideMm: materialIncrementMm / 2,
  };
}

function sameTubeCalibration(left: TubeProductV1, right: TubeProductV1): boolean {
  return left.diameterMm === right.diameterMm
    && left.developmentBaseMm === right.developmentBaseMm
    && left.materialIncrementMm === right.materialIncrementMm
    && left.developmentEffectiveMm === right.developmentEffectiveMm
    && left.extensionPerSideMm === right.extensionPerSideMm;
}

function shiftTransformX(transform: TransformMmV1, deltaX: number): TransformMmV1 {
  return { ...transform, cxMm: transform.cxMm + deltaX };
}

export interface TubeCalibrationMigrationResult {
  readonly document: ProjectDocumentV1;
  readonly migrated: boolean;
  readonly previousDevelopmentMm: number | null;
  readonly currentDevelopmentMm: number | null;
}

function usesLegacyFixedCanvasCalibration(product: TubeProductV1): boolean {
  if (product.materialId !== 'matte-canvas' && product.materialId !== 'glossy-canvas') {
    return false;
  }

  const preset = findTubePreset(product.presetCode);
  return preset !== undefined
    && product.diameterMm === preset.diameterMm
    && product.developmentBaseMm === preset.developmentBaseMm
    && product.materialIncrementMm === 4
    && product.developmentEffectiveMm === preset.developmentBaseMm + 4
    && product.extensionPerSideMm === 2;
}

export function migrateTubeDocumentToCurrentCalibration(
  document: ProjectDocumentV1,
): TubeCalibrationMigrationResult {
  if (document.product.kind !== 'tube') {
    return {
      document,
      migrated: false,
      previousDevelopmentMm: null,
      currentDevelopmentMm: null,
    };
  }

  const previousProduct = document.product;
  const currentProduct = createTubeProductV1(
    getTubePreset(previousProduct.presetCode),
    getPlotMaterial(previousProduct.materialId),
    previousProduct.heightMm,
  );
  if (sameTubeCalibration(previousProduct, currentProduct)) {
    return {
      document,
      migrated: false,
      previousDevelopmentMm: previousProduct.developmentEffectiveMm,
      currentDevelopmentMm: currentProduct.developmentEffectiveMm,
    };
  }

  const deltaX = (currentProduct.developmentEffectiveMm - previousProduct.developmentEffectiveMm) / 2;
  return {
    document: {
      ...document,
      product: currentProduct,
      background: document.background.kind === 'custom'
        ? { ...document.background, transform: shiftTransformX(document.background.transform, deltaX) }
        : document.background,
      nodes: document.nodes.map((node) => ({
        ...node,
        transform: shiftTransformX(node.transform, deltaX),
      })),
    },
    migrated: true,
    previousDevelopmentMm: previousProduct.developmentEffectiveMm,
    currentDevelopmentMm: currentProduct.developmentEffectiveMm,
  };
}

export function migrateLegacyFixedCanvasDocumentToCurrentCalibration(
  document: ProjectDocumentV1,
): TubeCalibrationMigrationResult {
  if (document.product.kind !== 'tube' || !usesLegacyFixedCanvasCalibration(document.product)) {
    return {
      document,
      migrated: false,
      previousDevelopmentMm: document.product.kind === 'tube'
        ? document.product.developmentEffectiveMm
        : null,
      currentDevelopmentMm: null,
    };
  }

  return migrateTubeDocumentToCurrentCalibration(document);
}

export type ResolveTubeProductResult =
  | {
      readonly ok: true;
      readonly value: TubeProductV1;
    }
  | {
      readonly ok: false;
      readonly error: HeightValidationResult & { readonly ok: false };
    };

export function resolveTubeProduct(input: {
  readonly presetCode: string;
  readonly materialId?: string;
  readonly heightInput: string | number;
}): ResolveTubeProductResult {
  const height = parseHeightCm(input.heightInput);
  if (!height.ok) {
    return { ok: false, error: height };
  }

  return {
    ok: true,
    value: createTubeProductV1(
      getTubePreset(input.presetCode),
      getPlotMaterial(input.materialId ?? DEFAULT_PLOT_MATERIAL_ID),
      height.millimeters,
    ),
  };
}
